import axios from 'axios';

const getToken = () => localStorage.getItem('token');

const api = axios.create({ timeout: 15000 });

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Routes that should NEVER trigger auto-logout even on 401
// (sub-requests, data lookups, GPS, history, device data, etc.)
const NO_LOGOUT_PATTERNS = [
  '/admin/vehicle',
  '/admin/gps/',
  '/admin/user/',
  '/admin/kyc/',
  '/admin/complaint/',
  '/admin/ticket/',
  '/admin/station/',
  '/admin/employee/',
  '/admin/notification',
  '/admin/vehicles/',
  '/admin/stations',
  '/admin/employees',
  '/admin/users',
  '/admin/dashboard',
  '/admin/vendor/',      // all vendor routes — dashboard, payment plans, collections
  '/admin/payment/',     // all payment routes
  '/admin/report',
  '/report/',
  '/getDevice',
  '/device/',
];

// Phrases that are genuine token errors (vs. 401 from a data endpoint returning no result)
const TOKEN_ERROR_PHRASES = ['expired', 'Invalid token', 'invalid token', 'Signature', 'Malformed'];

// Throttle logout — only redirect once per session, not on every failed sub-request
let logoutScheduled = false;

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const url    = err.config?.url ?? '';
    const status = err.response?.status;

    // Only process 401s
    if (status === 401 && !logoutScheduled) {
      const msg = err.response?.data?.message ?? '';

      // Skip logout if this is a data/sub-request URL
      const isDataRoute = NO_LOGOUT_PATTERNS.some(p => url.includes(p));

      // Only logout on genuine token errors from non-data routes
      if (!isDataRoute && TOKEN_ERROR_PHRASES.some(p => msg.includes(p))) {
        logoutScheduled = true;
        localStorage.clear();
        // Small delay so in-flight requests can settle before redirect
        setTimeout(() => {
          logoutScheduled = false; // reset so future logins work
          window.location.href = '/login';
        }, 500);
      }
    }

    return Promise.reject(err);
  }
);

export const InvokeApi = async (url, params = {}) => {
  try {
    return await api.post(url, params);
  } catch (err) {
    return {
      code: String(err?.response?.status ?? '500'),
      message: err?.response?.data?.message || err.message || 'Request failed',
    };
  }
};

export const postImg = async (url, formData) => {
  try {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    // Do NOT set Content-Type — let the browser set multipart boundary
    return await api.post(url, formData, { headers });
  } catch (err) {
    return {
      code: String(err?.response?.status ?? '500'),
      message: err?.response?.data?.message || err.message || 'Upload failed',
    };
  }
};

export const InvokeGetApi = async (url) => {
  try {
    return await api.get(url);
  } catch (err) {
    return null;
  }
};

export const apiList = {
  // Auth
  adminLogin:       '/auth/admin/login',
  changePassword:   '/auth/change-password',

  // Dashboard
  getDashboardStats:'/admin/dashboard',
  getVendorDashboard:'/admin/vendor/dashboard',

  // Users
  getUsers:         '/admin/users',
  getUser:          '/admin/user',
  getUserGPS:       '/admin/gps/user',
  getUserCalls:     '/admin/calls/user',
  getUserCallLogs:  '/admin/user/call-logs',
  getUserContacts:  '/admin/user/contacts',
  getUserHistory:   '/admin/user/history',
  createUser:       '/admin/user/create',
  editUser:         '/admin/user/edit',
  deleteUser:       '/admin/user/delete',
  toggleUser:       '/admin/user/toggle',
  uploadUserImage:  '/admin/user/upload-image',

  // Vehicles
  getVehicles:      '/admin/vehicles',
  getVehicle:       '/admin/vehicle',
  getVehicleHistory:'/admin/vehicle/history',
  getVehicleGPS:    '/admin/gps/vehicle',
  getUnassignedVehicles: '/admin/vehicles/unassigned',
  addVehicle:       '/admin/vehicle/add',
  editVehicle:      '/admin/vehicle/edit',
  deleteVehicle:    '/admin/vehicle/delete',
  restoreVehicle:   '/admin/vehicle/restore',
  uploadVehicleImage: '/admin/vehicle/upload-image',

  // KYC
  getKYCList:       '/admin/kyc',
  getKYCDetail:     '/admin/kyc/detail',
  getKYCByUser:     '/admin/kyc/by-user',
  approveKYC:       '/admin/kyc/approve',
  rejectKYC:        '/admin/kyc/reject',
  assignVehicle:    '/admin/kyc/assign-vehicle',

  // Employees
  getEmployees:     '/admin/employees',
  addEmployee:      '/admin/employee/add',
  editEmployee:     '/admin/employee/edit',
  toggleEmployee:   '/admin/employee/toggle',

  // Role Manager
  getRoles:         '/admin/roles',
  addRole:          '/admin/role/add',
  editRole:         '/admin/role/edit',
  deleteRole:       '/admin/role/delete',
  getRolePermissions: '/admin/role/permissions',

  // Vendors
  getVendors:       '/admin/vendors',

  // Vendor Payments (company → vendor)
  addVendorPaymentPlan:        '/admin/vendor/payment/plan/add',
  editVendorPaymentPlan:       '/admin/vendor/payment/plan/edit',
  getVendorPaymentPlans:       '/admin/vendor/payment/plans',
  addVendorPaymentCollection:  '/admin/vendor/payment/collection/add',
  editVendorPaymentCollection: '/admin/vendor/payment/collection/edit',
  getVendorPaymentCollections: '/admin/vendor/payment/collections',
  getVendorPaymentSummary:     '/admin/vendor/payment/summary',
  getVendorPaymentBreakdown:   '/admin/vendor/payment/breakdown',

  // Payments
  addPaymentPlan:        '/admin/payment/plan/add',
  editPaymentPlan:       '/admin/payment/plan/edit',
  getPaymentPlans:       '/admin/payment/plans',
  addPaymentCollection:  '/admin/payment/collection/add',
  editPaymentCollection: '/admin/payment/collection/edit',
  getPaymentCollections: '/admin/payment/collections',
  sendPaymentReminder:   '/admin/payment/reminder',
  getVehiclePaymentSummary: '/admin/vehicle/payment-summary',
  getPaymentStats:       '/admin/payment/stats',
  getPaymentBreakdownVendor: '/admin/payment/breakdown/vendor',
  getPaymentBreakdownUser:   '/admin/payment/breakdown/user',
  getPaymentBreakdownVehicle: '/admin/payment/breakdown/vehicle',

  // Battery Stations
  getStations:      '/admin/stations',
  addStation:       '/admin/station/add',
  editStation:      '/admin/station/edit',

  // Complaints
  getComplaints:    '/admin/complaints',
  updateComplaint:  '/admin/complaint/update',

  // Tickets
  getTickets:       '/admin/tickets',
  updateTicket:     '/admin/ticket/update',

  // Notifications
  getNotifications: '/admin/notifications',
  sendNotification: '/admin/notification/send',

  // Device Data (admin read)
  getDevices:           '/getDevices',
  getDeviceLocations:   '/getDeviceLocations',
  getDeviceCallLogs:    '/getDeviceCallLogs',
  getDeviceContacts:    '/getDeviceContacts',
  getDevicePhotos:      '/getDevicePhotos',
  latestLocationUser:   '/device/latest-location-user',

  // Vehicle path / summary (single source of truth — used by VehicleDetail + FleetMovementReport)
  getVehicleSummary:     '/admin/vehicle/summary',
  getVehiclePathHistory: '/admin/vehicle/path-history',

  // Reports
  vehicleAssignmentReport: '/report/vehicle-assignments',
  fleetMovementReport:     '/report/fleet-movement',

  // Loconav GPS Tracking
  loconavVehicles:    '/loconav/vehicles',
  loconavTelematics:  '/loconav/telematics',
  loconavVendorTelematics: '/loconav/vendor-telematics',
  loconavTimeline:    '/loconav/timeline',
  loconavScoreCard:   '/loconav/score-card',
  loconavDistance:    '/loconav/distance',
  loconavFleetReport: '/loconav/fleet-report',
  loconavSyncDay:     '/loconav/sync-day',
  loconavSyncStatus:  '/loconav/sync-status',
  loconavSyncVehicle: '/loconav/sync-vehicle',
  loconavDbPath:      '/loconav/db-path',
  loconavDbStats:     '/loconav/db-stats',
  loconavVehicleDetail:  '/loconav/vehicle-detail',
  loconavDbMonthly:      '/loconav/db-monthly',
};
